import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, Chip, Divider, Typography } from '@mui/material';
import { WButton } from '@wareclouds/wareclouds-ui';

import {
  OnboardingPageLayout,
  onboardingSecondaryContainedButtonSx,
} from '../../../shared';
import { useTrainingSection } from '../../hooks';
import { QuestionStep, StepNavigator, VideoStep } from '../components';

export default function TrainingSectionPage() {
  const { id: slug } = useParams();
  const sectionId = slug.replaceAll('-', '_');
  const { t } = useTranslation(['onboarding', 'button']);

  const {
    sectionTitle,
    sectionPart,
    currentStep,
    totalSteps,
    isVideoStep,
    isLastStep,
    handlePrev,
    handleNext,
    videoId,
    currentQuestion,
    questionNumber,
    selectedOption,
    handleSelectOption,
    handleSaveAndReturn,
    handleBackToMenu,
    allQuestionsAnswered,
    isSavePending,
  } = useTrainingSection(sectionId);

  const footer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {isLastStep && (
        <WButton
          label={t('save_answers', { ns: 'button' })}
          variant="contained"
          color="primary"
          fullWidth
          isLoading={isSavePending}
          isDisabled={!allQuestionsAnswered || isSavePending}
          onClick={handleSaveAndReturn}
        />
      )}
      <WButton
        label={t('back_to_menu', { ns: 'button' })}
        variant="contained"
        fullWidth
        onClick={handleBackToMenu}
        sx={onboardingSecondaryContainedButtonSx}
      />
    </Box>
  );

  return (
    <OnboardingPageLayout
      footer={footer}
      contentPb="calc(140px + env(safe-area-inset-bottom, 0px))"
    >
      <Chip
        label={t('training.section.badge', { n: sectionPart })}
        variant="outlined"
        color="primary"
        size="small"
        sx={{ mb: 1.5, borderRadius: 5 }}
      />

      <Typography variant="h5" fontWeight={500} sx={{ mt: 1, mb: 0.5 }}>
        {sectionTitle}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {isVideoStep ? (
        <VideoStep
          videoId={videoId}
          description={t('training.section.video_description')}
        />
      ) : (
        <QuestionStep
          question={currentQuestion}
          questionNumber={questionNumber}
          selectedOption={selectedOption}
          onSelect={handleSelectOption}
        />
      )}

      <StepNavigator
        currentStep={currentStep}
        totalSteps={totalSteps}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </OnboardingPageLayout>
  );
}
